import React from 'react';
import { Link } from 'react-router-dom';
import { Book, FacebookLogo, InstagramLogo, TwitterLogo, LinkedinLogo, YoutubeLogo } from '@phosphor-icons/react';

export default function Footer() {
  return (
    <footer className="bg-card border-t border-border py-12 px-6 mt-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-8">
          {/* Brand Column */}
          <div className="col-span-2">
            <div className="flex items-center gap-2 mb-3">
              <Book size={24} weight="duotone" className="text-primary" />
              <p className="text-xl font-semibold" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Kavyalekh
              </p>
            </div>
            <p className="text-sm text-muted-foreground max-w-xs mb-4">
              A premier platform celebrating Hindi literature — from legendary classics to contemporary voices.
            </p>
            <p className="text-xs text-muted-foreground">support@kavyalekh.com</p>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
              Company
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/page/about" className="hover:text-primary">
                  About
                </Link>
              </li>
              <li>
                <Link to="/page/mission" className="hover:text-primary">
                  Mission
                </Link>
              </li>
              <li>
                <Link to="/page/team" className="hover:text-primary">
                  Our Team
                </Link>
              </li>
              <li>
                <Link to="/page/careers" className="hover:text-primary">
                  Careers
                </Link>
              </li>
              <li>
                <Link to="/page/partnerships" className="hover:text-primary">
                  Partnerships
                </Link>
              </li>
              <li>
                <Link to="/page/advertise" className="hover:text-primary">
                  Advertise
                </Link>
              </li>
            </ul>
          </div>

          {/* Explore Links */}
          <div>
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
              Explore
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/authors" className="hover:text-primary">
                  Authors
                </Link>
              </li>
              <li>
                <Link to="/browse?category=Poem" className="hover:text-primary">
                  Poems
                </Link>
              </li>
              <li>
                <Link to="/browse?category=Story" className="hover:text-primary">
                  Stories
                </Link>
              </li>
              <li>
                <Link to="/collections" className="hover:text-primary">
                  Collections
                </Link>
              </li>
              <li>
                <Link to="/competitions" className="hover:text-primary">
                  Competitions
                </Link>
              </li>
            </ul>
          </div>

          {/* Writers Links */}
          <div>
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
              Writers
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/page/become-a-writer" className="hover:text-primary">
                  Become a Writer
                </Link>
              </li>
              <li>
                <Link to="/page/writer-guidelines" className="hover:text-primary">
                  Writer Guidelines
                </Link>
              </li>
              <li>
                <Link to="/page/reader-guidelines" className="hover:text-primary">
                  Reader Guidelines
                </Link>
              </li>
              <li>
                <Link to="/page/editorial-policy" className="hover:text-primary">
                  Editorial Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
              Support
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/page/help-center" className="hover:text-primary">
                  Help Center
                </Link>
              </li>
              <li>
                <Link to="/page/faq" className="hover:text-primary">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/page/safety-security" className="hover:text-primary">
                  Safety & Security
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-primary">
                  Contact
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Legal & Social Footer */}
        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Legal Links */}
            <div className="flex flex-wrap justify-center md:justify-start gap-x-4 gap-y-2 text-xs text-muted-foreground">
              <span>© 2026 Kavyalekh. All rights reserved.</span>
              <Link to="/page/privacy-policy" className="hover:text-primary">
                Privacy
              </Link>
              <Link to="/page/terms-of-use" className="hover:text-primary">
                Terms
              </Link>
              <Link to="/page/cookie-policy" className="hover:text-primary">
                Cookies
              </Link>
              <Link to="/page/copyright-policy" className="hover:text-primary">
                Copyright
              </Link>
              <Link to="/page/dmca-policy" className="hover:text-primary">
                DMCA
              </Link>
              <Link to="/page/code-of-conduct" className="hover:text-primary">
                Code of Conduct
              </Link>
              <Link to="/page/accessibility-statement" className="hover:text-primary">
                Accessibility
              </Link>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-3">
              <a
                href="https://facebook.com/kavyalekh"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="Facebook"
              >
                <FacebookLogo size={18} weight="fill" />
              </a>
              <a
                href="https://instagram.com/kavyalekh"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="Instagram"
              >
                <InstagramLogo size={18} weight="fill" />
              </a>
              <a
                href="https://twitter.com/kavyalekh"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="Twitter/X"
              >
                <TwitterLogo size={18} weight="fill" />
              </a>
              <a
                href="https://linkedin.com/company/kavyalekh"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="LinkedIn"
              >
                <LinkedinLogo size={18} weight="fill" />
              </a>
              <a
                href="https://youtube.com/@kavyalekh"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-muted hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="YouTube"
              >
                <YoutubeLogo size={18} weight="fill" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
